import { create } from "zustand";
import type { Event, EventStatistics } from "@/types/event";
import type { Guest } from "@/types/guest";
import type { GuestList } from "@/types/guest";
import type { Invitation } from "@/types/invitation";
import { eventService } from "@/services/eventService";
import { guestService } from "@/services/guestService";
import { guestListService } from "@/services/guestListService";
import { invitationService } from "@/services/invitationService";

export type EventTab =
  | "overview"
  | "guests"
  | "guest-lists"
  | "invitations"
  | "statistics"
  | "settings";

interface GuestFilter {
  listId?: string;
  checkInStatus?: "all" | "checked-in" | "not-checked-in";
}

interface EventDetailState {
  // Core data
  event: Event | null;
  guests: Guest[];
  guestLists: GuestList[];
  invitations: Invitation[];
  statistics: EventStatistics | null;

  // UI state
  activeTab: EventTab;
  guestSearch: string;
  guestFilter: GuestFilter;

  // Loading states (per-tab)
  isLoadingEvent: boolean;
  isLoadingGuests: boolean;
  isLoadingGuestLists: boolean;
  isLoadingInvitations: boolean;
  isLoadingStatistics: boolean;

  error: string | null;

  // Fetch operations
  fetchEvent: (id: string) => Promise<void>;
  fetchGuests: (eventId: string) => Promise<void>;
  fetchGuestLists: (eventId: string) => Promise<void>;
  fetchInvitations: (eventId: string) => Promise<void>;
  fetchStatistics: (eventId: string) => Promise<void>;

  // Guest operations
  checkInGuest: (guestId: string, totalPeople?: number) => Promise<void>;
  checkOutGuest: (guestId: string) => Promise<void>;

  // WebSocket handlers
  handleGuestUpdate: (guest: Guest) => void;
  handleGuestDelete: (guestId: string) => void;
  handleStatisticsUpdate: (stats: EventStatistics) => void;

  // UI actions
  setActiveTab: (tab: EventTab) => void;
  setGuestSearch: (search: string) => void;
  setGuestFilter: (filter: GuestFilter) => void;

  /** Which event the cached data belongs to; null before the first fetch. */
  loadedEventId: string | null;

  // Cleanup
  reset: () => void;
}

export const useEventDetailStore = create<EventDetailState>((set) => ({
  // Initial state
  event: null,
  guests: [],
  guestLists: [],
  invitations: [],
  statistics: null,

  activeTab: "overview",
  guestSearch: "",
  guestFilter: {},

  loadedEventId: null,

  // Start as loading, not idle. A page mounts, renders once with these flags,
  // and only then does its effect kick off the fetch — so `false` here paints
  // an empty-state flash before the skeleton ever appears. Every page-local
  // loading state in the app already starts at `true`; these now match.
  isLoadingEvent: true,
  isLoadingGuests: true,
  isLoadingGuestLists: true,
  isLoadingInvitations: true,
  isLoadingStatistics: true,

  error: null,

  // Fetch event
  fetchEvent: async (id: string) => {
    dropStaleEventData(id);
    set({ isLoadingEvent: true, error: null });

    try {
      const event = await eventService.getById(id);
      set({
        event,
        isLoadingEvent: false,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch event";
      set({
        error: message,
        isLoadingEvent: false,
      });
      throw error;
    }
  },

  // Fetch guests
  fetchGuests: async (eventId: string) => {
    dropStaleEventData(eventId);
    set({ isLoadingGuests: true, error: null });

    try {
      const guests = await guestService.getAll({ eventId });
      set({
        guests: guests || [],
        isLoadingGuests: false,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch guests";
      set({
        error: message,
        isLoadingGuests: false,
      });
      throw error;
    }
  },

  // Fetch guest lists
  fetchGuestLists: async (eventId: string) => {
    dropStaleEventData(eventId);
    set({ isLoadingGuestLists: true, error: null });

    try {
      const guestLists = await guestListService.getByEvent(eventId);
      set({
        guestLists: guestLists || [],
        isLoadingGuestLists: false,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch guest lists";
      set({
        error: message,
        isLoadingGuestLists: false,
      });
      throw error;
    }
  },

  // Fetch invitations
  fetchInvitations: async (eventId: string) => {
    dropStaleEventData(eventId);
    set({ isLoadingInvitations: true, error: null });

    try {
      const invitations = await invitationService.getByEvent(eventId);
      set({
        invitations: invitations || [],
        isLoadingInvitations: false,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch invitations";
      set({
        error: message,
        isLoadingInvitations: false,
      });
      throw error;
    }
  },

  // Fetch statistics
  fetchStatistics: async (eventId: string) => {
    dropStaleEventData(eventId);
    set({ isLoadingStatistics: true, error: null });

    try {
      const statistics = await eventService.getStatistics(eventId);
      set({
        statistics,
        isLoadingStatistics: false,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch statistics";
      set({
        error: message,
        isLoadingStatistics: false,
      });
      throw error;
    }
  },

  // Check-in guest
  checkInGuest: async (guestId: string, totalPeople?: number) => {
    const res = await guestService.checkIn(
      guestId,
      totalPeople != null ? { totalPeople } : undefined,
    );
    set((state) => ({
      guests: state.guests.map((g) =>
        g.id === guestId
          ? {
              ...g,
              checkedIn: true,
              checkedInFriends: res.checkedInFriends ?? g.checkedInFriends,
              checkedInAt: res.checkedInAt ?? new Date().toISOString(),
            }
          : g,
      ),
    }));
  },

  // Check-out guest
  checkOutGuest: async (guestId: string) => {
    await guestService.checkOut(guestId);
    set((state) => ({
      guests: state.guests.map((g) =>
        g.id === guestId
          ? { ...g, checkedIn: false, checkedInAt: undefined }
          : g,
      ),
    }));
  },

  // WebSocket handler - guest update
  handleGuestUpdate: (guest: Guest) => {
    set((state) => {
      const existingGuest = state.guests.find((g) => g.id === guest.id);

      if (existingGuest) {
        // Update existing guest
        return {
          guests: state.guests.map((g) => (g.id === guest.id ? guest : g)),
        };
      } else {
        // Add new guest
        return {
          guests: [...state.guests, guest],
        };
      }
    });
  },

  // WebSocket handler - guest delete
  handleGuestDelete: (guestId: string) => {
    set((state) => ({
      guests: state.guests.filter((g) => g.id !== guestId),
    }));
  },

  // WebSocket handler - statistics update
  handleStatisticsUpdate: (stats: EventStatistics) => {
    set({ statistics: stats });
  },

  // UI actions
  setActiveTab: (tab: EventTab) => {
    set({ activeTab: tab });
  },

  setGuestSearch: (search: string) => {
    set({ guestSearch: search });
  },

  setGuestFilter: (filter: GuestFilter) => {
    set({ guestFilter: filter });
  },

  // Reset - cleanup on unmount
  reset: () => {
    set({
      event: null,
      guests: [],
      guestLists: [],
      invitations: [],
      statistics: null,
      activeTab: "overview",
      guestSearch: "",
      guestFilter: {},
      isLoadingEvent: false,
      isLoadingGuests: false,
      isLoadingGuestLists: false,
      isLoadingInvitations: false,
      isLoadingStatistics: false,
      error: null,
      loadedEventId: null,
    });
  },
}));

/**
 * Wipe data belonging to a different event before its replacement loads.
 * Without this, navigating from one event to another paints the previous
 * event's guests/lists for a frame before the fetch flips the loading flags.
 */
function dropStaleEventData(eventId: string) {
  const s = useEventDetailStore.getState();
  if (s.loadedEventId === eventId) return;
  useEventDetailStore.setState({
    loadedEventId: eventId,
    event: null,
    guests: [],
    guestLists: [],
    invitations: [],
    statistics: null,
    guestSearch: "",
    guestFilter: {},
    isLoadingEvent: true,
    isLoadingGuests: true,
    isLoadingGuestLists: true,
    isLoadingInvitations: true,
    isLoadingStatistics: true,
    error: null,
  });
}

// Typed selectors to prevent over-subscription
export const useEventDetailEvent = () => useEventDetailStore((s) => s.event);
export const useEventDetailGuests = () => useEventDetailStore((s) => s.guests);
export const useEventDetailGuestLists = () =>
  useEventDetailStore((s) => s.guestLists);
export const useEventDetailInvitations = () =>
  useEventDetailStore((s) => s.invitations);
export const useEventDetailStatistics = () =>
  useEventDetailStore((s) => s.statistics);
export const useEventDetailActiveTab = () =>
  useEventDetailStore((s) => s.activeTab);
export const useEventDetailGuestSearch = () =>
  useEventDetailStore((s) => s.guestSearch);
export const useEventDetailGuestFilter = () =>
  useEventDetailStore((s) => s.guestFilter);
export const useEventDetailLoadingStates = () =>
  useEventDetailStore((s) => ({
    isLoadingEvent: s.isLoadingEvent,
    isLoadingGuests: s.isLoadingGuests,
    isLoadingGuestLists: s.isLoadingGuestLists,
    isLoadingInvitations: s.isLoadingInvitations,
    isLoadingStatistics: s.isLoadingStatistics,
  }));
export const useEventDetailActions = () =>
  useEventDetailStore((s) => ({
    fetchEvent: s.fetchEvent,
    fetchGuests: s.fetchGuests,
    fetchGuestLists: s.fetchGuestLists,
    fetchInvitations: s.fetchInvitations,
    fetchStatistics: s.fetchStatistics,
    checkInGuest: s.checkInGuest,
    checkOutGuest: s.checkOutGuest,
    handleGuestUpdate: s.handleGuestUpdate,
    handleGuestDelete: s.handleGuestDelete,
    handleStatisticsUpdate: s.handleStatisticsUpdate,
    setActiveTab: s.setActiveTab,
    setGuestSearch: s.setGuestSearch,
    setGuestFilter: s.setGuestFilter,
    reset: s.reset,
  }));
